import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import Box from "@mui/material/Box";

import { ModelType } from "../../../API";
import ConfigurationComponent from "../../../common/components/box/ConfigurationComponent";
import WarningBox from "../../../common/components/box/WarningBox";
import FullPageLoader from "../../../common/components/item/FullPageLoader";
import ItemDetailHeader from "../../../common/components/item/ItemDetailHeader";
import ItemDetailTable from "../../../common/components/item/ItemDetailTable";
import MainPaperWrapper from "../../../common/components/item/MainPaperWrapper";
import ResponsiveSplitContainer from "../../../common/components/item/ResponsiveSplitContainer";
import LiveViewContainer, {
  ViewMode,
  ViewModeEnum,
} from "../../../common/components/live-view/LiveViewContainer";
import BreadcrumbNavigation from "../../../common/components/tabs/BreadcrumbNavigation";
import { captureKeyframe } from "../../../common/helpers/captureKeyframe";
import { useAuthenticatedUser } from "../../../common/hooks/useAuthenticatedUser";
import { RoleEnum, RouteEnum } from "../../../common/models/enums";
import {
  errorNotification,
  successNotification,
} from "../../../common/variables/notification";
import { useSyncShadow } from "../../model-manager/hooks/useSyncShadow";
import { useUpdateService } from "../../model-manager/hooks/useUpdateService";
import { ServiceTypeEnum } from "../../model-manager/variables/modelManager";
import { useGetNodeById } from "../../nodes/hooks/useGetNodeById";
import AnnotationContainer from "../annotations/AnnotationContainer";
import { IDeviceTableRow, IModel } from "../hooks/useDeviceTableRows";
import { useGetAnnotations } from "../hooks/useGetAnnotations";
import useGetDeviceById from "../hooks/useGetDeviceByUrl";
import { usePublishNode } from "../hooks/usePublishNode";
import ModelActionControls from "./ModelActionControls";
import CaptureKeyframeButton from "../../../common/components/button/CaptureKeyframeButton";
import { DEFAULT_MEDIA_HEIGHT } from "../../../common/variables/common";

const OverviewModelPage = () => {
  const [deviceDetail, setDeviceDetail] = useState<IDeviceTableRow | null>(
    null
  );
  const [isKeyframeLoaded, setKeyframeLoaded] = useState(false);
  const [isModelRunning, setIsModelRunning] = useState(false);
  const [configurationParams, setConfigurationParams] = useState({
    editMode: false,
    value: "",
  });

  const { role } = useAuthenticatedUser();

  const [currentViewMode, setCurrentViewMode] = useState<ViewMode>(
    ViewModeEnum.KEYFRAME
  );

  const { fetchDevice, getServicesLoading, loading, isServiceLoaded } =
    useGetDeviceById();

  const {
    fetchNode,
    data: nodeData,
    loading: getNodeLoading,
  } = useGetNodeById();
  const { updateService } = useUpdateService();
  const { syncShadowsForNode } = useSyncShadow();
  const { deviceId } = useParams();

  const { publishNode } = usePublishNode();

  let { modelType } = useParams();

  const location = useLocation();

  if (modelType === ServiceTypeEnum.LEAK) {
    modelType = ServiceTypeEnum.GAS_LEAK;
  }

  const modelDetails = useMemo((): IModel | undefined => {
    const model = deviceDetail?.models.find(
      (model): boolean =>
        model.serviceName === modelType ||
        model.serviceName === "Leak" ||
        model.serviceName === ServiceTypeEnum.SAFETY_AND_SECURITY
    );

    if (model?.configuration) {
      setConfigurationParams({
        ...configurationParams,
        value: model.configuration,
      });

      const normalizedNodeId = model.nodeId?.startsWith("C_")
        ? model.nodeId
            .replace("C_", "C#")
            .replace("_L_", "#L#")
            .replace("_N_", "#N#")
        : model.nodeId;

      fetchNode(normalizedNodeId ?? "");
    }

    setIsModelRunning(!!model?.isRunning);

    return model;
  }, [deviceDetail]);

  const { data: fetchAnnotationsData } = useGetAnnotations({
    serviceId: modelDetails?.serviceId ?? "",
    deviceId: deviceDetail?.rowId ?? "",
  });

  useEffect((): void => {
    location.state = { deviceId: deviceId };

    if (!isServiceLoaded) return;

    fetchDevice()?.then((device): void => {
      if (device) {
        setDeviceDetail(device);

        setCurrentViewMode(ViewModeEnum.KEYFRAME_WITH_ANNOTATIONS);
      }
    });
  }, [isServiceLoaded]);

  const keyFrameOrRecentFrame = useMemo(() => {
    if (deviceDetail?.deviceData.hasPanTilt) {
      return deviceDetail?.deviceData.signedUrlRecentFrame ?? "";
    }

    return deviceDetail?.deviceData.signedUrlKeyFrame ?? "";
  }, [deviceDetail]);

  const hardHatObject =
    modelDetails?.modelAlertSettings?.modelAlertParameters?.find(
      item => item.objectType.toString().toLowerCase() === ModelType.hardhat
    );

  let annotationsData = fetchAnnotationsData?.getAnnotations?.items ?? [];

  if (
    (modelDetails?.serviceName === ServiceTypeEnum.SAFETY_AND_SECURITY ||
      modelDetails?.serviceName === ServiceTypeEnum.PERSONNEL_DETECTION) &&
    !hardHatObject?.enabled
  ) {
    annotationsData = [];
  }

  const showLoading =
    loading ||
    getServicesLoading ||
    getNodeLoading ||
    !annotationsData ||
    !deviceDetail ||
    !modelDetails;

  if (showLoading) {
    return <FullPageLoader />;
  }

  if (modelType === ServiceTypeEnum.GAS_LEAK) {
    modelDetails.serviceName = ServiceTypeEnum.LEAK;
  }

  if (modelType === ServiceTypeEnum.SAFETY_AND_SECURITY) {
    modelDetails.serviceName = ServiceTypeEnum.PERSONNEL_DETECTION;
  }

  const cancelConfigurationEdit = (): void => {
    setConfigurationParams({ ...configurationParams, editMode: false });
  };

  const saveConfigurationEdit = (value: string): void => {
    updateService({
      configuration: value,
      serviceId: modelDetails.serviceId,
      nodeId: modelDetails.nodeId,
    }).finally((): void => {
      syncShadowsForNode(modelDetails.nodeId);

      setConfigurationParams({ value, editMode: false });

      successNotification("Changes applied");
    });
  };

  const setIsModelRunningStatus = (value: boolean): void => {
    setIsModelRunning(value);
  };

  const handleCaptureKeyframe = (): void => {
    if (deviceDetail) {
      captureKeyframe(deviceDetail, publishNode);
    }
  };

  const hasDevicePt = deviceDetail.deviceData?.hasPanTilt;

  const tableData = [
    {
      label: "CONFIGURATION PARAMETERS",
      value: configurationParams.value,
      jsonFormat: true,
      editButton: true,
      rowStyles: { alignItems: "flex-start" },
      onEditHandler: (): void => {
        if (
          role !== RoleEnum.ROOT &&
          role !== RoleEnum.CUSTOMER_ADMIN &&
          role !== RoleEnum.PARTNER_ADMIN &&
          role !== RoleEnum.CC_OPS_ADMIN &&
          role !== RoleEnum.CC_OPS
        ) {
          errorNotification("You do not have permissions to access.");

          return;
        }

        setConfigurationParams({ ...configurationParams, editMode: true });
      },
    },
  ];

  const modelName =
    modelDetails.serviceName === ServiceTypeEnum.SAFETY_AND_SECURITY ||
    modelDetails.serviceName === ServiceTypeEnum.PERSONNEL_DETECTION
      ? ServiceTypeEnum.PERSONNEL_DETECTION
      : modelDetails.serviceName;

  const breadcrumbItems = [
    { label: "Devices", path: RouteEnum.Devices },
    {
      label: deviceDetail.name,
      path: "/device/" + encodeURIComponent(deviceDetail.rowId),
    },
    { label: modelName },
  ];

  const nodeDetailsRow = [
    {
      label: "NODE",
      value: nodeData?.nodeName ?? "",
    },
  ];

  const isCaptureKeyFramesDisabled =
    (!hasDevicePt ? false : !isModelRunning) ||
    !deviceDetail?.deviceData.recentFrame ||
    !deviceDetail?.isOnline ||
    currentViewMode === ViewModeEnum.LIVE;

  let tooltipTitle =
    !isModelRunning && isCaptureKeyFramesDisabled
      ? "New key image capturing will be available when model is enabled"
      : "The model will be stopped while the key image is capturing";

  if (!deviceDetail?.isOnline) {
    tooltipTitle =
      "New key image capturing will be available when device is online";
  }

  const runningLeakModels = deviceDetail.models.filter(
    item =>
      item.isRunning &&
      (item.serviceName === ServiceTypeEnum.LEAK ||
        item.serviceName === ServiceTypeEnum.GAS_LEAK ||
        item.serviceName === ServiceTypeEnum.LIQUID_LEAK)
  );

  return (
    <>
      <BreadcrumbNavigation items={breadcrumbItems} />

      <ItemDetailHeader name={modelName}>
        <ModelActionControls
          device={deviceDetail}
          isModelRunning={isModelRunning}
          setIsModelRunning={setIsModelRunningStatus}
          deviceModel={modelDetails}
          runningLeakModels={runningLeakModels}
        />
      </ItemDetailHeader>

      <MainPaperWrapper>
        <ResponsiveSplitContainer gap="1.5em">
          <Box>
            {!isModelRunning && (
              <Box sx={{ marginBottom: "0.5em" }}>
                <WarningBox>
                  This model isn’t running. To receive all notifications from
                  this model, please enable it
                </WarningBox>
              </Box>
            )}

            <ItemDetailTable data={nodeDetailsRow} />

            {!configurationParams.editMode && (
              <ItemDetailTable data={tableData} />
            )}

            {configurationParams.editMode && (
              <ConfigurationComponent
                model={modelType ?? ""}
                params={configurationParams.value}
                onCancelHandler={cancelConfigurationEdit}
                onSaveHandler={saveConfigurationEdit}
              />
            )}

            {!hasDevicePt && (
              <AnnotationContainer
                deviceId={deviceDetail.rowId}
                nodeId={deviceDetail.node.id}
                annotationsData={annotationsData}
                serviceId={modelDetails.serviceId}
                serviceType={modelDetails.serviceName}
                keyFrame={keyFrameOrRecentFrame}
                isKeyframeLoaded={isKeyframeLoaded}
                setKeyframeLoaded={setKeyframeLoaded}
                isHardHatObjectEnabled={hardHatObject?.enabled}
              />
            )}
          </Box>

          <Box>
            {!hasDevicePt && (
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <CaptureKeyframeButton
                  tooltipTitle={tooltipTitle}
                  label="Capture a new key image"
                  onClick={handleCaptureKeyframe}
                  disabled={isCaptureKeyFramesDisabled}
                />
              </Box>
            )}

            <LiveViewContainer
              s3Key={keyFrameOrRecentFrame}
              device={deviceDetail}
              showPtButton={hasDevicePt}
              fixedHeight={DEFAULT_MEDIA_HEIGHT}
              annotationsData={annotationsData}
              isKeyframeLoadedDefaultValue={isKeyframeLoaded}
              defaultViewMode={ViewModeEnum.KEYFRAME_WITH_ANNOTATIONS}
              viewMode={currentViewMode}
              setCurrentViewMode={setCurrentViewMode}
            />
          </Box>
        </ResponsiveSplitContainer>
      </MainPaperWrapper>
    </>
  );
};

export default OverviewModelPage;
